import React, { useState } from "react";
import "./styles/login.css";
import { toast } from "react-hot-toast";
import {
  loginCustomer,
  registerCustomer,
  requestCustomerPasswordReset,
  resetCustomerPassword,
} from "../api/customerAccountApi";
import { useLanguage } from "../context/languagecontext.jsx";

function LogIn() {
  const { t } = useLanguage();
  const [isSignUpActive, setIsSignUpActive] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");

  // Quên mật khẩu state
  const [isForgotPasswordActive, setIsForgotPasswordActive] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1 = nhập SĐT/Email, 2 = nhập OTP & đặt lại mật khẩu mới
  const [forgotIdentifier, setForgotIdentifier] = useState(""); // SĐT hoặc Email
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Nhập liệu đăng nhập: có thể là SĐT hoặc Email
  const [loginIdentifier, setLoginIdentifier] = useState("");

  // Hàm kiểm tra định dạng số điện thoại (10 chữ số)
  const validatePhone = (phone) => {
    const re = /^\d{10}$/;
    return re.test(phone);
  };

  const handleToggle = () => {
    setIsSignUpActive((prev) => !prev);
    setIsForgotPasswordActive(false);
    setForgotPasswordStep(1);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error(t("passwords_do_not_match_signup", "Mật khẩu không khớp"));
      return;
    }
    if (!validatePhone(phone)) {
      toast.error(t("phone_validation_msg", "Số điện thoại phải có đúng 10 chữ số"));
      return;
    }
    if (!email) {
      toast.error(t("email_required_msg", "Vui lòng nhập email"));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error(t("email_validation_msg", "Email không đúng định dạng"));
      return;
    }

    const queryParams = new URLSearchParams(window.location.search);
    const redirectUrl = queryParams.get("redirect") || "";
    const segments = redirectUrl.split("/");
    const stationIndex = segments.indexOf("station");
    let inviteCode = "";
    if (stationIndex !== -1 && segments[stationIndex + 1]) {
      inviteCode = segments[stationIndex + 1];
    }

    const user = { name, email, phone, password, inviteCode };

    try {
      const response = await registerCustomer(user);

      if (response.ok) {
        toast.success(t("register_success", "Đăng ký thành công"));
        setName("");
        setEmail("");
        setPhone("");
        setPassword("");
        setConfirmPassword("");
        setIsSignUpActive(false);
      } else {
        toast.error(t("register_failed"));
      }
    } catch (error) {
      toast.error(t("error_occurred"));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginIdentifier) {
      toast.error(t("login_identifier_required", "Vui lòng nhập số điện thoại hoặc email"));
      return;
    }

    // Phát hiện xem đây là email hay SĐT
    const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginIdentifier);
    const isPhone = /^\d{10}$/.test(loginIdentifier);

    if (!isEmail && !isPhone) {
      toast.error(t("login_identifier_invalid", "Vui lòng nhập đúng số điện thoại (10 số) hoặc địa chỉ email"));
      return;
    }

    // Lấy inviteCode/stationCode từ sessionStorage hoặc URL redirect
    const activeStationCode = sessionStorage.getItem("activeStationCode");
    const queryParams = new URLSearchParams(window.location.search);
    const redirectUrl = queryParams.get("redirect") || "";
    const segments = redirectUrl.split("/");
    const stationIndex = segments.indexOf("station");
    let inviteCode = activeStationCode || "";
    if (stationIndex !== -1 && segments[stationIndex + 1]) {
      inviteCode = segments[stationIndex + 1];
    }

    const user = isEmail
      ? { email: loginIdentifier, password, inviteCode }
      : { phone: loginIdentifier, password, inviteCode };

    try {
      const response = await loginCustomer(user);

      if (response.ok) {
        toast.success(t("login_success", "Đăng nhập thành công"));
        const queryParams = new URLSearchParams(window.location.search);
        const redirectUrl = queryParams.get("redirect") || "/";
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1000);
      } else {
        toast.error(t("invalid_credentials", "Số điện thoại/Email hoặc mật khẩu không đúng"));
      }
    } catch (error) {
      toast.error(t("error_occurred"));
    }
  };

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    if (!forgotIdentifier) {
      toast.error(t("forgot_identifier_required", "Vui lòng nhập số điện thoại hoặc email"));
      return;
    }

    setLoading(true);
    try {
      const response = await requestCustomerPasswordReset(forgotIdentifier);

      if (response.ok) {
        toast.success(t("otp_sent_success", "Mã OTP đã được gửi"));
        setForgotPasswordStep(2);
      } else {
        toast.error(t("otp_send_failed"));
      }
    } catch (error) {
      toast.error(t("error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmNewPassword) {
      toast.error(t("passwords_do_not_match_signup", "Mật khẩu không khớp"));
      return;
    }
    if (!otp || otp.length < 6) {
      toast.error(t("otp_invalid", "Vui lòng nhập mã OTP gồm 6 chữ số"));
      return;
    }

    setLoading(true);
    try {
      const response = await resetCustomerPassword({
        identifier: forgotIdentifier,
        otp,
        newPassword,
      });

      if (response.ok) {
        toast.success(t("reset_password_success", "Đặt lại mật khẩu thành công"));
        setIsForgotPasswordActive(false);
        setForgotPasswordStep(1);
        setForgotIdentifier("");
        setOtp("");
        setNewPassword("");
        setConfirmNewPassword("");
      } else {
        toast.error(t("reset_password_failed"));
      }
    } catch (error) {
      toast.error(t("error_occurred"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-main-container" style={{ minHeight: "100vh" }}>
      <div
        className={`login-container ${isSignUpActive ? "active" : ""}`}
        id="login-container"
      >
        <div className="form-container sign-up">
          <form onSubmit={handleRegister}>
            <h1>{t("create_account", "Tạo tài khoản")}</h1>
            <input
              type="text"
              placeholder={t("full_name")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              type="tel"
              placeholder={t("phone_number")}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <input
              type="email"
              placeholder={t("email_address", "Địa chỉ email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder={t("password", "Mật khẩu")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <input
              type="password"
              placeholder={t("confirm_password", "Xác nhận mật khẩu")}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            <button type="submit">{t("register", "Đăng ký")}</button>
            <p className="mobile-toggle-text" onClick={handleToggle}>
              {t("already_have_account_login", "Đã có tài khoản? Đăng nhập ngay")}
            </p>
          </form>
        </div>

        <div className="form-container sign-in">
          {isForgotPasswordActive ? (
            forgotPasswordStep === 1 ? (
              <form onSubmit={handleRequestOtp}>
                <h1>{t("forgot_password_title", "Quên mật khẩu")}</h1>
                <p style={{ textAlign: "center", fontSize: "13px", color: "#666", marginBottom: "15px" }}>
                  {t("forgot_password_desc", "Nhập số điện thoại hoặc email của bạn để nhận mã OTP khôi phục mật khẩu.")}
                </p>
                <input
                  type="text"
                  placeholder={t("phone_or_email", "Số điện thoại hoặc Email")}
                  value={forgotIdentifier}
                  onChange={(e) => setForgotIdentifier(e.target.value)}
                  required
                />
                <p style={{ 
                  fontSize: "12px", 
                  color: "#d32f2f", 
                  marginTop: "-5px", 
                  marginBottom: "15px", 
                  textAlign: "left", 
                  width: "100%", 
                  fontStyle: "italic",
                  lineHeight: "1.4"
                }}>
                  {t("otp_email_note", "Lưu ý: Mã OTP sẽ gửi về email đăng ký của khách hàng. OTP có hiệu lực trong vòng 5 phút!")}
                </p>
                <button type="submit" disabled={loading}>
                  {loading ? t("processing") : t("send_otp", "Gửi mã OTP")}
                </button>
                <p
                  onClick={() => {
                    setIsForgotPasswordActive(false);
                    setForgotPasswordStep(1);
                  }}
                  style={{
                    marginTop: "15px",
                    fontSize: "13px",
                    color: "#0052ab",
                    cursor: "pointer",
                    fontWeight: "500",
                    textDecoration: "underline"
                  }}
                >
                  {t("back_to_login", "Quay lại đăng nhập")}
                </p>
              </form>
            ) : (
              <form onSubmit={handleResetPassword}>
                <h1>{t("reset_password_title", "Đặt lại mật khẩu")}</h1>
                <p style={{ textAlign: "center", fontSize: "13px", color: "#666", marginBottom: "15px" }}>
                  {t("otp_sent_to", "Mã OTP đã được gửi đến email liên kết của tài khoản:")} <strong>{forgotIdentifier}</strong>
                </p>
                <input
                  type="text"
                  placeholder={t("otp_placeholder", "Nhập mã OTP 6 số")}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  maxLength={6}
                  required
                />
                <div style={{ position: "relative", width: "100%" }}>
                  <input
                    type={showNewPassword ? "text" : "password"}
                    placeholder={t("new_password", "Mật khẩu mới")}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    style={{ paddingRight: "40px" }}
                  />
                  <span
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "55%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#666",
                      fontSize: "16px",
                      zIndex: 10
                    }}
                  >
                    <i className={showNewPassword ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"}></i>
                  </span>
                </div>
                <div style={{ position: "relative", width: "100%" }}>
                  <input
                    type={showConfirmNewPassword ? "text" : "password"}
                    placeholder={t("confirm_new_password", "Nhập lại mật khẩu mới")}
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    required
                    style={{ paddingRight: "40px" }}
                  />
                  <span
                    onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "55%",
                      transform: "translateY(-50%)",
                      cursor: "pointer",
                      color: "#666",
                      fontSize: "16px",
                      zIndex: 10
                    }}
                  >
                    <i className={showConfirmNewPassword ? "fa-solid fa-eye-slash" : "fa-solid fa-eye"}></i>
                  </span>
                </div>
                <button type="submit" disabled={loading}>
                  {loading ? t("processing") : t("confirm_reset", "Xác nhận")}
                </button>
                <p
                  onClick={() => setForgotPasswordStep(1)}
                  style={{
                    marginTop: "15px",
                    fontSize: "13px",
                    color: "#0052ab",
                    cursor: "pointer",
                    fontWeight: "500",
                    textDecoration: "underline"
                  }}
                >
                  {t("back", "Quay lại")}
                </p>
              </form>
            )
          ) : (
            <form onSubmit={handleLogin}>
              <h1>{t("login")}</h1>
              <input
                type="text"
                placeholder={t("phone_or_email", "Số điện thoại hoặc Email")}
                value={loginIdentifier}
                onChange={(e) => setLoginIdentifier(e.target.value)}
              />
              <input
                type="password"
                placeholder={t("password", "Mật khẩu")}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p
                onClick={() => {
                  setIsForgotPasswordActive(true);
                  setForgotPasswordStep(1);
                  setForgotIdentifier(loginIdentifier); // auto fill nếu họ đã nhập
                }}
                style={{
                  alignSelf: "flex-end",
                  margin: "5px 0 15px",
                  fontSize: "13px",
                  color: "#0052ab",
                  cursor: "pointer",
                  fontWeight: "500",
                  textDecoration: "underline"
                }}
              >
                {t("forgot_password", "Quên mật khẩu?")}
              </p>
              <button type="submit">{t("login")}</button>
              <p className="mobile-toggle-text" onClick={handleToggle}>
                {t("dont_have_account_register", "Chưa có tài khoản? Đăng ký ngay")}
              </p>
            </form>
          )}
        </div>

        <div className="login-toggle-container">
          <div className="login-toggle">
            <div className="login-toggle-panel login-toggle-left">
              <h1>{t("welcome", "Chào mừng!")}</h1>
              <p>{t("fill_to_register", "Hãy điền thông tin để tạo tài khoản")}</p>
              <button onClick={handleToggle} className="hidden" id="login">
                {t("login")}
              </button>
            </div>
            <div className="login-toggle-panel login-toggle-right">
              <h1>{t("hello", "Xin chào!")}</h1>
              <p>{t("login_to_use_all_features", "Hãy đăng nhập để sử dụng hết các tính năng")}</p>
              <button onClick={handleToggle} className="hidden" id="register">
                {t("register", "Đăng ký")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LogIn;
